/* eslint-disable simple-import-sort/imports */
import React, {useCallback, useEffect, useState} from 'react';
import {useLocation} from 'react-router-dom';

import ProfileMenu from '../Search/SearchNav/ProfileMenu';
import wbLogo from './assets/wb-cw.svg';
import menuIcon from './assets/icon-menu.svg';
import closeIcon from './assets/icon-close.svg';
import greaterThanIcon from './assets/greater-than.svg';
import * as S from '../../pages/FC.styles';
import {useViewer} from '../../state/viewer/hooks';
import './FCTopNav.css';
import {
  ENTERPRISE_DROPDOWN_CONFIG,
  PLATFORM_DROPDOWN_CONFIG,
  RESOURCES_DROPDOWN_CONFIG,
  SOLUTIONS_DROPDOWN_CONFIG,
} from './config';
import {
  type FCTopNavProps,
  type MegaMenuDropdownColumn,
  type MegaMenuDropdownItem,
  type MegaMenuDropdownProps,
  hasDescription,
} from './types';

const MegaMenuDropdown = ({config, className}: MegaMenuDropdownProps) => {
  const topColumns = config.filter(c => c.placement !== 'bottom');
  const bottomColumns = config.filter(c => c.placement === 'bottom');

  return (
    <div className={`wandb-sub-nav ${className || ''}`}>
      {topColumns.map((column: MegaMenuDropdownColumn, colIndex: number) => (
        <ul key={colIndex}>
          <li>
            <div className="wandb-nav-sub-title">
              {column.isLinkableTitle && column.url ? (
                <S.TopNavDropdownItem as="a" href={column.url}>
                  {column.title}
                  <img
                    src={greaterThanIcon}
                    alt=""
                    style={{
                      width: '10px',
                      height: '19px',
                      marginLeft: '8px',
                      display: 'inline-block',
                    }}
                  />
                </S.TopNavDropdownItem>
              ) : (
                column.title
              )}
            </div>
            <ul className="wandb-nav-sub-items">
              {column.items.map(
                (item: MegaMenuDropdownItem, itemIndex: number) =>
                  item.icon ? (
                    <li className="wbnav-icon-list-item" key={itemIndex}>
                      <S.TopNavDropdownItem as="a" href={item.url}>
                        <span className="wbnav-icon-list-icon">
                          {item.icon && (
                            <img
                              src={item.icon}
                              alt={`${item.name} icon`}
                              onError={e => {
                                // Hide missing/broken icons to keep menu resilient
                                e.currentTarget.style.display = 'none';
                              }}
                              style={{
                                width: '100%',
                                height: 'auto',
                                display: 'block',
                              }}
                            />
                          )}
                        </span>
                        <span className="wbnav-icon-list-text">
                          {item.name}
                          <br />
                          {hasDescription(item) && (
                            <span>{item.description}</span>
                          )}
                        </span>
                      </S.TopNavDropdownItem>
                    </li>
                  ) : (
                    <li key={itemIndex}>
                      <S.TopNavDropdownItem as="a" href={item.url}>
                        {item.name}
                      </S.TopNavDropdownItem>
                    </li>
                  )
              )}
            </ul>
          </li>
        </ul>
      ))}

      {bottomColumns.length > 0 &&
        bottomColumns.map((column: MegaMenuDropdownColumn, idx: number) => (
          <ul key={`bottom-${idx}`} className="wandb-nav-bottom-fw">
            <li>
              <div className="wandb-nav-sub-title">{column.title}</div>
              <ul className="wandb-nav-sub-items">
                {column.items.map((item, itemIndex) => (
                  <li className="wbnav-icon-list-item" key={itemIndex}>
                    <S.TopNavDropdownItem as="a" href={item.url}>
                      <span className="wbnav-icon-list-icon">
                        {item.icon && (
                          <img
                            src={item.icon}
                            alt={`${item.name} icon`}
                            onError={e => {
                              e.currentTarget.style.display = 'none';
                            }}
                            style={{
                              width: '100%',
                              height: 'auto',
                              display: 'block',
                            }}
                          />
                        )}
                      </span>
                      <span className="wbnav-icon-list-text">
                        {item.name}
                        <br />
                        {hasDescription(item) && (
                          <span>{item.description}</span>
                        )}
                      </span>
                    </S.TopNavDropdownItem>
                  </li>
                ))}
              </ul>
            </li>
          </ul>
        ))}
    </div>
  );
};

export const FCTopNav: React.FC<FCTopNavProps> = React.memo(() => {
  const viewer = useViewer();
  const [mobileNavMenuOpen, setMobileNavMenuOpen] = useState(false);
  const [openSection, setOpenSection] = useState<
    null | 'platform' | 'solutions' | 'enterprise' | 'resources'
  >(null);

  const closeMobileMenu = useCallback(() => {
    setMobileNavMenuOpen(false);
    setOpenSection(null);
  }, []);

  useEffect(() => {
    if (mobileNavMenuOpen) {
      document.body.classList.add('show-mobile-menu');
    } else {
      document.body.classList.remove('show-mobile-menu');
    }
  }, [mobileNavMenuOpen]);

  const location = useLocation();
  useEffect(() => {
    closeMobileMenu();
  }, [location, closeMobileMenu]);

  return (
    <>
      <header className="wandb-header">
        <div className="container">
          <div className="header-cols">
            <div className="header-left">
              <div className="header-logo">
                <a href="https://wandb.ai/site">
                  <img src={wbLogo} alt="WandB Logo" />
                </a>
              </div>
              <nav className="wandb-nav wb-header-hidden-mobile">
                <ul>
                  <li className="wb-menu-has-children">
                    <span className="wb-menu-item">Platform</span>
                    <MegaMenuDropdown
                      config={PLATFORM_DROPDOWN_CONFIG}
                      className="three-columns four-columns"
                    />
                  </li>
                  <li className="wb-menu-has-children">
                    <span className="wb-menu-item">Solutions</span>
                    <MegaMenuDropdown
                      config={SOLUTIONS_DROPDOWN_CONFIG}
                      className="three-columns allow-paddings"
                    />
                  </li>
                  <li className="wb-menu-has-children">
                    <span className="wb-menu-item">Enterprise</span>
                    <div className="wandb-sub-nav single-column">
                      <ul>
                        {ENTERPRISE_DROPDOWN_CONFIG.items.map((item, i) => (
                          <li key={i}>
                            {/* eslint-disable-next-line wandb/no-a-tags */}
                            <a href={item.url}>{item.name}</a>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </li>
                  <li className="wb-menu-has-children">
                    <span className="wb-menu-item">Resources</span>
                    <div
                      className="wandb-sub-nav single-column"
                      style={RESOURCES_DROPDOWN_CONFIG.style}>
                      <ul style={RESOURCES_DROPDOWN_CONFIG.ulStyle}>
                        {RESOURCES_DROPDOWN_CONFIG.items.map((item, i) => (
                          <li key={i}>
                            {/* eslint-disable-next-line wandb/no-a-tags */}
                            <a href={item.url}>{item.name}</a>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </li>
                  <li>
                    <a href="https://docs.wandb.ai/" className="wb-menu-item">
                      Docs
                    </a>
                  </li>
                  <li>
                    <a
                      href="https://wandb.ai/site/pricing/"
                      className="wb-menu-item">
                      Pricing
                    </a>
                  </li>
                </ul>
              </nav>
            </div>
            <div className="header-right">
              <div className="header-ctas wb-header-hidden-mobile">
                <a
                  href="https://wandb.ai/site/contact/"
                  className="contact-link">
                  Contact
                </a>
                {viewer ? (
                  <div className="profile-menu search-nav">
                    <ProfileMenu isFullyConnected={true} />
                  </div>
                ) : (
                  <>
                    <a href="https://app.wandb.ai/login">Log In</a>
                    <a
                      href="https://app.wandb.ai/login?signup=true"
                      className="yellow-btn">
                      Sign Up
                    </a>
                  </>
                )}
              </div>
              <button
                type="button"
                className="wb-header-hidden-desktop mobile-menu-toggle"
                onClick={e => {
                  e.preventDefault();
                  setMobileNavMenuOpen(prev => !prev);
                }}>
                <img className="menu-icon" src={menuIcon} alt="Menu" />
                <img className="close-icon" src={closeIcon} alt="Close" />
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="mobile-menu-wrapper">
        <div className="mobile-menu-inner">
          <div className="mobile-menu-items">
            {(
              [
                {
                  id: 'platform' as const,
                  label: 'Platform',
                  render: () => (
                    <>
                      {PLATFORM_DROPDOWN_CONFIG.map(col => (
                        <div key={col.title} className="mobile-menu-section">
                          {col.url ? (
                            <div className="wandb-nav-sub-title">
                              {/* eslint-disable-next-line wandb/no-a-tags */}
                              <a href={col.url} onClick={closeMobileMenu}>
                                {col.title} &gt;
                              </a>
                            </div>
                          ) : (
                            <div className="wandb-nav-sub-title">
                              {col.title}
                            </div>
                          )}
                          <ul className="mobile-menu-section-items">
                            {col.items.map(item => (
                              <li
                                key={item.url}
                                className="mobile-menu-item-with-icon">
                                {/* eslint-disable-next-line wandb/no-a-tags */}
                                <a href={item.url} onClick={closeMobileMenu}>
                                  {item.icon && (
                                    <div className="mobile-menu-item-icon">
                                      <img
                                        src={item.icon}
                                        alt=""
                                        width="40"
                                        height="40"
                                      />
                                    </div>
                                  )}
                                  <div className="mobile-menu-item-content">
                                    <div className="mobile-menu-item-title">
                                      {item.name}
                                    </div>
                                    {hasDescription(item) && (
                                      <div className="mobile-menu-item-description">
                                        {item.description}
                                      </div>
                                    )}
                                  </div>
                                </a>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </>
                  ),
                },
                {
                  id: 'solutions' as const,
                  label: 'Solutions',
                  render: () => (
                    <>
                      {SOLUTIONS_DROPDOWN_CONFIG.map(col => (
                        <div key={col.title} className="mobile-menu-section">
                          {col.url ? (
                            <div className="wandb-nav-sub-title">
                              {/* eslint-disable-next-line wandb/no-a-tags */}
                              <a href={col.url} onClick={closeMobileMenu}>
                                {col.title} &gt;
                              </a>
                            </div>
                          ) : (
                            <div className="wandb-nav-sub-title">
                              {col.title}
                            </div>
                          )}
                          <ul className="mobile-menu-section-items">
                            {col.items.map(item => (
                              <li
                                key={item.url}
                                className="mobile-menu-item-with-icon">
                                {/* eslint-disable-next-line wandb/no-a-tags */}
                                <a href={item.url} onClick={closeMobileMenu}>
                                  {item.icon && (
                                    <div className="mobile-menu-item-icon">
                                      <img
                                        src={item.icon}
                                        alt=""
                                        width="40"
                                        height="40"
                                      />
                                    </div>
                                  )}
                                  <div className="mobile-menu-item-content">
                                    <div className="mobile-menu-item-title">
                                      {item.name}
                                    </div>
                                    {hasDescription(item) && (
                                      <div className="mobile-menu-item-description">
                                        {item.description}
                                      </div>
                                    )}
                                  </div>
                                </a>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </>
                  ),
                },
                {
                  id: 'enterprise' as const,
                  label: 'Enterprise',
                  render: () => (
                    <div className="mobile-menu-section">
                      <ul className="mobile-menu-section-items">
                        {ENTERPRISE_DROPDOWN_CONFIG.items.map(item => (
                          <li
                            key={item.url}
                            className="mobile-menu-item-with-icon">
                            {/* eslint-disable-next-line wandb/no-a-tags */}
                            <a href={item.url} onClick={closeMobileMenu}>
                              <div className="mobile-menu-item-content">
                                <div className="mobile-menu-item-title">
                                  {item.name}
                                </div>
                                {hasDescription(item) && (
                                  <div className="mobile-menu-item-description">
                                    {item.description}
                                  </div>
                                )}
                              </div>
                            </a>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ),
                },
                {
                  id: 'resources' as const,
                  label: 'Resources',
                  render: () => (
                    <div className="mobile-menu-section">
                      <ul className="mobile-menu-section-items">
                        {RESOURCES_DROPDOWN_CONFIG.items.map(item => (
                          <li
                            key={item.url}
                            className="mobile-menu-item-with-icon">
                            {/* eslint-disable-next-line wandb/no-a-tags */}
                            <a href={item.url} onClick={closeMobileMenu}>
                              <div className="mobile-menu-item-content">
                                <div className="mobile-menu-item-title">
                                  {item.name}
                                </div>
                                {hasDescription(item) && (
                                  <div className="mobile-menu-item-description">
                                    {item.description}
                                  </div>
                                )}
                              </div>
                            </a>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ),
                },
              ] as const
            ).map(section => (
              <div
                className={`mobile-menu-item has-sub-nav ${openSection === section.id ? 'open' : ''}`}
                key={section.id}>
                {/* eslint-disable-next-line wandb/no-a-tags, jsx-a11y/anchor-is-valid */}
                <a
                  href="#"
                  role="button"
                  onClick={(e: React.MouseEvent) => {
                    e.preventDefault();
                    setOpenSection(prev =>
                      prev === section.id ? null : section.id
                    );
                  }}>
                  {section.label}
                </a>
                <div
                  className="wb-sub-items-wrapper single-column"
                  style={{
                    display: openSection === section.id ? 'block' : 'none',
                  }}>
                  {section.render()}
                </div>
              </div>
            ))}

            <div className="mobile-menu-item">
              {/* eslint-disable-next-line wandb/no-a-tags */}
              <a href="https://docs.wandb.ai/" onClick={closeMobileMenu}>
                Docs
              </a>
            </div>
            <div className="mobile-menu-item">
              {/* eslint-disable-next-line wandb/no-a-tags */}
              <a
                href="https://wandb.ai/site/pricing/"
                onClick={closeMobileMenu}>
                Pricing
              </a>
            </div>
          </div>
          <div className="mobile-menu-buttons">
            {viewer ? (
              <S.TopNavLink to="/settings" className="yellow-btn">
                Profile
              </S.TopNavLink>
            ) : (
              <>
                <a href="https://wandb.ai/site/contact/" className="yellow-btn">
                  Contact
                </a>
                <a
                  href="https://app.wandb.ai/login?_gl=1*1njlh40*_ga*MjAzMDY0NTMxOC4xNjg2NzMzODEw*_ga_JH1SJHJQXJ*MTG9MDk5MDgxNS4xMjEuMS4xNjkwOTkxMDExLjYwLjAuMA.."
                  className="yellow-btn">
                  Log In
                </a>
                <a
                  href="https://app.wandb.ai/login?signup=true&_gl=1*1njlh40*_ga*MjAzMDY0NTMxOC4xNjg2NzMzODEw*_ga_JH1SJHJQXJ*MTG9MDk5MDgxNS4xMjEuMS4xNjkwOTkxMDExLjYwLjAuMA.."
                  className="yellow-btn">
                  Sign Up
                </a>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
});

export default FCTopNav;
